import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  MessageCircle,
  Send,
  Image,
  Video,
  Paperclip,
  Search,
  User,
  X,
  CheckCheck,
  Clock,
  FileImage,
  FileVideo,
} from "lucide-react";
import { useSearchParams } from "react-router-dom";
import useToken from "../../../../hooks/auth/useToken";
import { chatService, ChatConversation, ChatMessage } from "../../services/chatService";
import { API_BASE } from "../../../../utils/api";
import { Layout } from "../../layouts/DashboardLayout";

const MAX_IMAGE_SIZE_MB = 5;
const MAX_VIDEO_SIZE_MB = 20;

const ChatPage: React.FC = () => {
  const { token } = useToken();
  const [searchParams] = useSearchParams();

  // State
  const [conversations, setConversations] = useState<ChatConversation[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [newMessage, setNewMessage] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [filePreview, setFilePreview] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sending, setSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const openedFromQuery = useRef<string | null>(null);

  // Refs pour la gestion du scroll
  const prevLastMessageIdRef = useRef<string | number | null>(null);
  const isNearBottomRef = useRef(true);

  // Détecte si l'utilisateur est proche du bas
  const handleScroll = () => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    isNearBottomRef.current = distanceFromBottom < 100;
  };

  // Force le scroll tout en bas (instantané ou smooth)
  const scrollToBottom = useCallback((behavior: ScrollBehavior = "auto") => {
    const el = scrollContainerRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior });
  }, []);

  // Load conversations
  const loadConversations = useCallback(async () => {
    if (!token) return;
    try {
      const data = await chatService.getConversations(token);
      setConversations(data.conversations || []);
    } catch (error) {
      console.error("Erreur chargement conversations:", error);
    }
  }, [token]);

  // Load messages for a user
  const loadMessages = useCallback(
    async (userId: string, showLoading = true) => {
      if (!token) return;
      if (showLoading) setLoadingMessages(true);
      try {
        const data = await chatService.getMessages(token, userId, 0, 200);
        setMessages((data.messages || []).reverse());
        await chatService.markAsRead(token, userId);
        setConversations((prev) => prev.map((c) => (c.user_id === userId ? { ...c, unread_count: 0 } : c)));
      } catch (error) {
        console.error("Erreur chargement messages:", error);
      } finally {
        setLoadingMessages(false);
      }
    },
    [token]
  );

  // Initial load
  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  // Poll for new messages every 5s
  useEffect(() => {
    if (pollingRef.current) clearInterval(pollingRef.current);

    pollingRef.current = setInterval(() => {
      loadConversations();
      if (selectedUserId) {
        loadMessages(selectedUserId, false);
      }
    }, 5000);

    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, [selectedUserId, loadConversations, loadMessages]);

  // Scroll intelligent : ne scrolle que si nouveau message ET utilisateur en bas
  useEffect(() => {
    const last = messages[messages.length - 1];
    const lastId = last?.id ?? null;

    // Rien de nouveau → on ne touche pas au scroll
    if (lastId === prevLastMessageIdRef.current) return;

    const isFirstLoad = prevLastMessageIdRef.current === null;
    prevLastMessageIdRef.current = lastId;

    if (isFirstLoad) {
      // Premier chargement : on force en bas immédiatement,
      // puis on re-force après le rendu des images/vidéos
      isNearBottomRef.current = true;
      scrollToBottom("auto");
      requestAnimationFrame(() => scrollToBottom("auto"));
      setTimeout(() => scrollToBottom("auto"), 100);
      setTimeout(() => scrollToBottom("auto"), 300);
    } else if (isNearBottomRef.current) {
      // Nouveau message et utilisateur en bas → scroll smooth
      scrollToBottom("smooth");
    }
  }, [messages, scrollToBottom]);

  // Select a conversation
  const handleSelectConversation = (userId: string) => {
    setSelectedUserId(userId);
    setMessages([]); // vide d'abord
    prevLastMessageIdRef.current = null; // force isFirstLoad au prochain setMessages
    isNearBottomRef.current = true;
    loadMessages(userId);
  };

  // Handle file selection
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const ext = file.name.split(".").pop()?.toLowerCase() || "";
    const isImage = ["jpg", "jpeg", "png", "gif", "webp"].includes(ext);
    const isVideo = ["mp4", "mov", "avi", "webm"].includes(ext);

    if (!isImage && !isVideo) {
      alert("Type de fichier non supporté. Images: jpg, png, gif, webp. Vidéos: mp4, mov, avi, webm");
      return;
    }

    const sizeMB = file.size / (1024 * 1024);
    if (isImage && sizeMB > MAX_IMAGE_SIZE_MB) {
      alert(`Image trop volumineuse. Maximum: ${MAX_IMAGE_SIZE_MB} MB`);
      return;
    }
    if (isVideo && sizeMB > MAX_VIDEO_SIZE_MB) {
      alert(`Vidéo trop volumineuse. Maximum: ${MAX_VIDEO_SIZE_MB} MB`);
      return;
    }

    setSelectedFile(file);
    if (isImage) {
      const reader = new FileReader();
      reader.onload = (ev) => setFilePreview(ev.target?.result as string);
      reader.readAsDataURL(file);
    } else {
      setFilePreview(null);
    }
  };

  // Clear file selection
  const clearFile = () => {
    setSelectedFile(null);
    setFilePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // Send message
  const handleSend = async () => {
    if (!token || !selectedUserId) return;
    if (!newMessage.trim() && !selectedFile) return;

    setSending(true);
    try {
      await chatService.sendMessage(token, selectedUserId, newMessage.trim() || undefined, selectedFile || undefined);
      setNewMessage("");
      clearFile();
      // On force le scroll en bas après envoi
      isNearBottomRef.current = true;
      await loadMessages(selectedUserId, false);
      await loadConversations();
      scrollToBottom("smooth");
    } catch (error) {
      console.error("Erreur envoi message:", error);
      alert("Erreur lors de l'envoi du message");
    } finally {
      setSending(false);
    }
  };

  // Handle Enter key
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Filter + tri : non lus d'abord, puis plus recent. Le backend trie deja
  // ainsi, on le refait ici pour rester correct meme si le polling renvoie
  // une page mal ordonnee ou si la recherche filtre la liste.
  const filteredConversations = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const filtered = conversations.filter((c) => {
      if (!q) return true;
      return (
        c.user_first_name?.toLowerCase().includes(q) ||
        c.user_last_name?.toLowerCase().includes(q) ||
        c.user_email?.toLowerCase().includes(q) ||
        c.user_phonenumber?.includes(q)
      );
    });
    return filtered.sort((a, b) => {
      const unreadA = Number(a.unread_count) || 0;
      const unreadB = Number(b.unread_count) || 0;
      if (unreadB > 0 !== unreadA > 0) return unreadB > 0 ? 1 : -1;
      if (unreadB !== unreadA) return unreadB - unreadA;
      const timeA = a.last_message_time ? new Date(a.last_message_time).getTime() : 0;
      const timeB = b.last_message_time ? new Date(b.last_message_time).getTime() : 0;
      return timeB - timeA;
    });
  }, [conversations, searchQuery]);

  // Ouverture directe depuis une notification (`?user=`).
  useEffect(() => {
    const userId = searchParams.get("user");
    if (!userId || openedFromQuery.current === userId) return;
    if (!conversations.some((c) => c.user_id === userId)) return;
    openedFromQuery.current = userId;
    setSelectedUserId(userId);
    setMessages([]);
    prevLastMessageIdRef.current = null;
    isNearBottomRef.current = true;
    loadMessages(userId);
  }, [searchParams, conversations, loadMessages]);

  // Get selected conversation
  const selectedConversation = conversations.find((c) => c.user_id === selectedUserId);

  // Format time
  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return "À l'instant";
    if (diffMins < 60) return `${diffMins}min`;
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays < 7) return `${diffDays}j`;
    return date.toLocaleDateString("fr-FR");
  };

  const formatMessageTime = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString("fr-FR", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Total unread
  const totalUnread = conversations.reduce((acc, c) => acc + (Number(c.unread_count) || 0), 0);

  return (
    <Layout>
      <div className="flex flex-col h-[calc(100vh-64px)]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white">
          <div className="flex items-center gap-3">
            <MessageCircle className="w-6 h-6 text-green-600" />
            <div>
              <h1 className="text-xl font-bold text-gray-900">Chat Support</h1>
              <p className="text-sm text-gray-500">
                {conversations.length} conversation(s)
                {totalUnread > 0 && (
                  <span className="ml-2 px-2 py-0.5 text-xs font-medium text-white bg-red-500 rounded-full">
                    {totalUnread} non lu(s)
                  </span>
                )}
              </p>
            </div>
          </div>
        </div>

        {/* Main content */}
        <div className="flex flex-1 overflow-hidden">
          {/* Left panel - Conversations list */}
          <div className="w-80 border-r border-gray-200 bg-white flex flex-col">
            {/* Search */}
            <div className="p-3 border-b border-gray-100">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Rechercher un utilisateur..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Conversations */}
            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center h-32">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600" />
                </div>
              ) : filteredConversations.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-32 text-gray-400">
                  <MessageCircle className="w-8 h-8 mb-2" />
                  <p className="text-sm">Aucune conversation</p>
                </div>
              ) : (
                filteredConversations.map((conv) => (
                  <button
                    key={conv.user_id}
                    onClick={() => handleSelectConversation(conv.user_id)}
                    className={`w-full flex items-start gap-3 p-3 border-b border-gray-50 hover:bg-gray-50 transition-colors text-left ${
                      selectedUserId === conv.user_id ? "bg-green-50 border-l-4 border-l-green-600" : ""
                    }`}
                  >
                    {/* Avatar */}
                    <div className="flex-shrink-0 w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                      <User className="w-5 h-5 text-green-700" />
                    </div>
                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-semibold text-gray-900 truncate">
                          {conv.user_first_name} {conv.user_last_name}
                        </p>
                        {conv.last_message_time && (
                          <span className="text-xs text-gray-400 flex-shrink-0 ml-1">
                            {formatTime(conv.last_message_time)}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 truncate">{conv.user_email}</p>
                      <div className="flex items-center justify-between mt-1">
                        <p className="text-xs text-gray-400 truncate">
                          {conv.last_message_type === "IMAGE"
                            ? "📷 Image"
                            : conv.last_message_type === "VIDEO"
                              ? "🎥 Vidéo"
                              : conv.last_message || "..."}
                        </p>
                        {(Number(conv.unread_count) || 0) > 0 && (
                          <span className="flex-shrink-0 ml-1 px-1.5 py-0.5 text-xs font-bold text-white bg-red-500 rounded-full min-w-[20px] text-center">
                            {conv.unread_count > 99 ? "99+" : conv.unread_count}
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>

          {/* Right panel - Chat area */}
          <div className="flex-1 flex flex-col bg-gray-50">
            {selectedUserId && selectedConversation ? (
              <>
                {/* Chat header */}
                <div className="flex items-center gap-3 px-6 py-3 bg-white border-b border-gray-200">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <User className="w-5 h-5 text-green-700" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">
                      {selectedConversation.user_first_name} {selectedConversation.user_last_name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {selectedConversation.user_email} • {selectedConversation.user_phonenumber}
                    </p>
                  </div>
                </div>

                {/* Messages - avec ref et onScroll */}
                <div
                  ref={scrollContainerRef}
                  onScroll={handleScroll}
                  className="flex-1 overflow-y-auto px-6 py-4 space-y-3"
                >
                  {loadingMessages ? (
                    <div className="flex items-center justify-center h-full">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600" />
                    </div>
                  ) : messages.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-gray-400">
                      <MessageCircle className="w-12 h-12 mb-3" />
                      <p>Aucun message dans cette conversation</p>
                    </div>
                  ) : (
                    messages.map((msg) => (
                      <div
                        key={msg.id}
                        className={`flex ${msg.sender_type === "ADMIN" ? "justify-end" : "justify-start"}`}
                      >
                        <div
                          className={`max-w-[70%] rounded-2xl px-4 py-2.5 ${
                            msg.sender_type === "ADMIN"
                              ? "bg-green-600 text-white rounded-br-md"
                              : "bg-white text-gray-900 shadow-sm border border-gray-100 rounded-bl-md"
                          }`}
                        >
                          {/* Admin name badge */}
                          {msg.sender_type === "ADMIN" && msg.admin_name && (
                            <p className="text-xs opacity-75 mb-1">{msg.admin_name}</p>
                          )}

                          {/* File content */}
                          {msg.message_type === "IMAGE" && msg.file_url && (
                            <div className="mb-2">
                              <img
                                src={`${API_BASE}${msg.file_url}`}
                                alt="Image"
                                className="rounded-lg max-w-full max-h-60 object-cover cursor-pointer"
                                onClick={() => window.open(`${API_BASE}${msg.file_url}`, "_blank")}
                              />
                            </div>
                          )}
                          {msg.message_type === "VIDEO" && msg.file_url && (
                            <div className="mb-2">
                              <video
                                src={`${API_BASE}${msg.file_url}`}
                                controls
                                className="rounded-lg max-w-full max-h-60"
                              />
                            </div>
                          )}

                          {/* Text content */}
                          {msg.content && <p className="text-sm whitespace-pre-wrap break-words">{msg.content}</p>}

                          {/* Time + read status */}
                          <div
                            className={`flex items-center gap-1 mt-1 ${
                              msg.sender_type === "ADMIN" ? "justify-end" : "justify-start"
                            }`}
                          >
                            <span
                              className={`text-[10px] ${
                                msg.sender_type === "ADMIN" ? "text-green-200" : "text-gray-400"
                              }`}
                            >
                              {formatMessageTime(msg.created_at)}
                            </span>
                            {msg.sender_type === "ADMIN" && (
                              <CheckCheck className={`w-3 h-3 ${msg.is_read ? "text-blue-200" : "text-green-300"}`} />
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* File preview */}
                {selectedFile && (
                  <div className="px-6 py-2 bg-white border-t border-gray-100">
                    <div className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg">
                      {filePreview ? (
                        <img src={filePreview} alt="Preview" className="w-12 h-12 rounded object-cover" />
                      ) : (
                        <div className="w-12 h-12 bg-blue-100 rounded flex items-center justify-center">
                          <FileVideo className="w-6 h-6 text-blue-600" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-700 truncate">{selectedFile.name}</p>
                        <p className="text-xs text-gray-400">{(selectedFile.size / (1024 * 1024)).toFixed(2)} MB</p>
                      </div>
                      <button onClick={clearFile} className="p-1 hover:bg-gray-200 rounded-full">
                        <X className="w-4 h-4 text-gray-500" />
                      </button>
                    </div>
                  </div>
                )}

                {/* Input area */}
                <div className="px-6 py-3 bg-white border-t border-gray-200">
                  <div className="flex items-end gap-3">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpg,image/jpeg,image/png,image/gif,image/webp,video/mp4,video/mov,video/avi,video/webm"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="flex-shrink-0 p-2.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-full transition-colors"
                      title="Joindre un fichier (Image max 5MB, Vidéo max 20MB)"
                    >
                      <Paperclip className="w-5 h-5" />
                    </button>

                    <textarea
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder="Écrire un message..."
                      rows={1}
                      className="flex-1 px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-2xl text-sm resize-none focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent max-h-32"
                      style={{ minHeight: "42px" }}
                    />

                    <button
                      onClick={handleSend}
                      disabled={sending || (!newMessage.trim() && !selectedFile)}
                      className="flex-shrink-0 p-2.5 bg-green-600 text-white rounded-full hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {sending ? (
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                      ) : (
                        <Send className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                  <p className="text-[10px] text-gray-400 mt-1 ml-12">
                    Images: max {MAX_IMAGE_SIZE_MB}MB • Vidéos: max {MAX_VIDEO_SIZE_MB}MB
                  </p>
                </div>
              </>
            ) : (
              /* No conversation selected */
              <div className="flex flex-col items-center justify-center h-full text-gray-400">
                <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                  <MessageCircle className="w-12 h-12 text-gray-300" />
                </div>
                <p className="text-lg font-medium text-gray-500">Chat Support</p>
                <p className="text-sm text-gray-400 mt-1">Sélectionnez une conversation pour commencer</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ChatPage;
